import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, HelpCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SITE } from "../lib/site";
import { useLocation as useGeo } from "../contexts/LocationContext";
import SectionHeading from "../components/SectionHeading";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function FAQ() {
  const { displayCity, city } = useGeo();
  useDocumentTitle(
    `FAQ — Électricien ${city ? `à ${city}` : displayCity} | Voltis Électricité`,
    "Questions fréquentes : urgence électrique, types de pannes, installations, devis et zone d'intervention. Réponses claires de votre électricien."
  );

  const FAQ_ITEMS = [
    {
      q: "Intervenez-vous en urgence ?",
      a: "Oui, nous intervenons rapidement pour les dépannages électriques. Notre équipe est disponible 24h/24, 7j/7, week-ends et jours fériés inclus. Délai d'arrivée moyen : 30 minutes en zone urbaine.",
    },
    {
      q: "Quels types de pannes réparez-vous ?",
      a: "Nous intervenons sur les coupures de courant, courts-circuits, disjoncteurs qui sautent, tableaux électriques, prises et éclairages. Nos techniciens identifient l'origine de la panne, sécurisent l'installation et procèdent à la réparation aux normes.",
    },
    {
      q: "Faites-vous aussi les installations électriques ?",
      a: "Oui, nous réalisons les installations électriques neuves, rénovations, mises aux normes NF C 15-100, tableaux électriques, prises et éclairages. Que ce soit pour une maison, un appartement, un commerce ou un bureau, nous prenons en charge l'ensemble de votre projet.",
    },
    {
      q: "Puis-je demander un devis ?",
      a: "Oui, un devis peut être proposé selon le type d'intervention. Pour les travaux d'installation, le devis est gratuit et remis sous 24 à 48 heures. Pour un dépannage, le tarif est annoncé clairement avant toute intervention.",
    },
    {
      q: "Dans quelles villes intervenez-vous ?",
      a: city
        ? `Nous intervenons à ${city} et dans toutes les communes voisines.`
        : "Nous intervenons près de chez vous et dans toutes les communes voisines.",
    },
    {
      q: "Quelles sont vos garanties ?",
      a: "Tous nos travaux sont garantis 2 ans (pièces et main-d'œuvre). Nous disposons également d'une assurance responsabilité civile professionnelle et d'une garantie décennale. Notre matériel est certifié CE et conforme à la norme NF C 15-100.",
    },
    {
      q: "Quels sont vos délais d'intervention ?",
      a: "En zone urbaine, nous arrivons en 30 minutes en moyenne pour les urgences. Pour les installations sur devis, l'intervention est planifiée selon votre disponibilité, généralement sous 48 à 72 heures.",
    },
    {
      q: "Travaillez-vous pour les professionnels ?",
      a: "Oui, nous intervenons pour les particuliers, les commerces, les bureaux, les copropriétés et les syndics. N'hésitez pas à nous demander un devis adapté à votre activité.",
    },
  ];

  return (
    <div data-testid="faq-page">
      {/* HERO */}
      <section className="bg-slate-50">
        <div className="container-x py-14 lg:py-20">
          <div className="max-w-3xl">
            <span className="accent-bar mb-5" />
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-slate-900 mb-3">
              Questions fréquentes
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              Toutes les réponses,{" "}
              <span className="text-[#EAB308]">en clair.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Urgence, dépannage, installation, devis, zones desservies — voici les
              questions que nos clients posent le plus souvent.
            </p>
          </div>
        </div>
      </section>

      {/* ACCORDION */}
      <section className="section-padding bg-white">
        <div className="container-x max-w-4xl">
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_ITEMS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`q-${i}`}
                data-testid={`faq-item-${i}`}
                className="border border-slate-200 bg-white px-6 rounded-sm hover:border-slate-900 transition-colors"
              >
                <AccordionTrigger className="text-left font-heading font-semibold text-slate-900 hover:no-underline py-5 text-base lg:text-lg">
                  <span className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-[#EAB308] mt-0.5 shrink-0" strokeWidth={2} />
                    <span>{f.q}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed pb-5 pl-8">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0F172A] text-white">
        <div className="container-x py-16 lg:py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <span className="accent-bar mb-5" />
            <h2 className="font-heading text-3xl lg:text-4xl font-bold leading-tight">
              Une question qui n'est pas dans la liste ?
            </h2>
            <p className="mt-3 text-slate-300 text-lg max-w-2xl">
              Appelez-nous ou envoyez-nous un message — nous répondons rapidement.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row gap-3 lg:justify-end">
            <a href={`tel:${SITE.phoneTel}`} data-testid="faq-call-btn" className="btn-primary justify-center">
              <Phone className="w-5 h-5" /> {SITE.phoneDisplay}
            </a>
            <Link to="/contact" className="bg-white text-slate-900 hover:bg-slate-100 inline-flex items-center justify-center gap-2 px-6 py-3.5 font-semibold rounded-sm transition-colors">
              Contact <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
